import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerLocale, getNames } from "i18n-iso-countries";
import enCountries from "i18n-iso-countries/langs/en.json";
import csCountries from "i18n-iso-countries/langs/cs.json";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { t } from "i18next";

import { Dialog } from "@components/common";
import { Button, MenuItem, Select, Stack, TextField } from "@components/base";
import { useGetCurrentCompanySettings, useUpdateCurrentCompanyAddress } from "@api";
import { useUserStore } from "@store/user";

registerLocale(enCountries);
registerLocale(csCountries);

type CountryOption = {
  code: string;
  name: string;
};

const schema = object({
  address: string().max(255).required(t("validation.required")),
  city: string().max(255).required(t("validation.required")),
  zip: string().required(t("validation.required")),
  country: string().required(t("validation.required")),
}).required();

interface CompanyAddressEditor {
  open: boolean;
  onClose: () => void;
}

const CompanyAddressEditor: FC<CompanyAddressEditor> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [countriesList, setCountriesList] = useState<CountryOption[]>([]);
  const { currentCompanySettings, currentCompanySettingsEnabled } =
    useGetCurrentCompanySettings(user);
  const {
    updateCurrentCompanyAddressAsync,
    updateCurrentCompanyAddressEnabled,
    isPendingUpdateCurrentCompanyAddress,
  } = useUpdateCurrentCompanyAddress(user);
  const methods = useForm<{
    address: string;
    city: string;
    zip: string;
    country: string;
  }>({
    resolver: yupResolver(schema),
  });

  const hasAddress =
    currentCompanySettings?.registeredAddress?.address ||
    currentCompanySettings?.registeredAddress?.city ||
    currentCompanySettings?.registeredAddress?.zip ||
    currentCompanySettings?.registeredAddress?.country;

  const handleSubmit = (): void => {
    methods.handleSubmit(onSubmit)();
  };

  const onSubmit = async (): Promise<void> => {
    if (currentCompanySettings) {
      const values = methods.getValues();

      await updateCurrentCompanyAddressAsync({
        registeredAddress: values,
      });
    }
    onClose();
  };

  const countryOptions = useMemo(() => {
    return countriesList.map((country, index) => (
      <MenuItem value={country.code} key={`country-${index}`}>
        {country.name}
      </MenuItem>
    ));
  }, [countriesList]);

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng") || "en";
    const countryNames = getNames(language);
    const countryCodes = Object.keys(countryNames).map((code) => {
      const name = countryNames[code];

      return {
        code,
        name,
      };
    });

    setCountriesList(countryCodes);
  }, []);

  useEffect(() => {
    if (currentCompanySettings?.registeredAddress) {
      methods.setValue(
        "country",
        currentCompanySettings.registeredAddress.country || "",
      );
      methods.setValue(
        "address",
        currentCompanySettings.registeredAddress.address || "",
      );
      methods.setValue("city", currentCompanySettings.registeredAddress.city || "");
      methods.setValue("zip", currentCompanySettings.registeredAddress.zip || "");
    }
  }, [currentCompanySettings]);

  return (
    currentCompanySettingsEnabled &&
    updateCurrentCompanyAddressEnabled && (
      <Dialog
        title={
          hasAddress
            ? t("organizationSettings.companyAddress.editAddress")
            : t("organizationSettings.companyAddress.addAddress")
        }
        isOpen={open}
        onClose={onClose}
        dialogProps={{ maxWidth: "sm", fullWidth: true }}
      >
        <Stack spacing={2}>
          <Controller
            name="address"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("organizationSettings.companyAddress.address")}
                error={!!error}
                helperText={error ? error.message : ""}
              />
            )}
          />
          <Controller
            name="city"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("organizationSettings.companyAddress.city")}
                error={!!error}
                helperText={error ? error.message : ""}
              />
            )}
          />
          <Controller
            name="zip"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("organizationSettings.companyAddress.zip")}
                error={!!error}
                helperText={error ? error.message : ""}
              />
            )}
          />
          <Controller
            name="country"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Select variant="filled" displayEmpty {...field} error={!!error}>
                <MenuItem value={undefined} disabled>
                  {t("organizationSettings.companyAddress.country")}
                </MenuItem>
                {countryOptions}
              </Select>
            )}
          />
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2} mt={3}>
          <Button variant="outlined" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isPendingUpdateCurrentCompanyAddress}
          >
            {t("common.save")}
          </Button>
        </Stack>
      </Dialog>
    )
  );
};

export default CompanyAddressEditor;
